import { Quaternion, Vector3 } from 'three';

import type { CartesianPose } from '@sb/geometry';
import { projectPointToPixel } from '@sb/geometry';
import type { CameraIntrinsics } from '@sb/integrations/types/cameraTypes';

import { DISPLAY_AXES } from './poseToDisplayFrame';

export interface PixelPoint {
  x: number;
  y: number;
}

export type GraspAxisKey = 'x' | 'y' | 'z';

/** A resolved grasp pose, drawn into the frame it was found in. */
export interface ProjectedGrasp {
  /** The pixel the pose projects to: where the arm is sent, offset included. */
  origin: PixelPoint;
  /** The tip of each display axis, less any culled for facing the camera. */
  axes: Array<{ key: GraspAxisKey; tip: PixelPoint }>;
}

// Each axis extends this far (meters) from the pose origin in 3D, then is
// projected through the camera, so the axes foreshorten the way the real frame
// does: one tilted toward the camera renders shorter, and nearer detections get
// larger triads than far ones. Trimmed to 70% — full-length arrows overlapped
// neighbouring detections.
const DEFAULT_AXIS_LENGTH_M = 0.05;
const AXIS_LENGTH_SCALE = 0.7;

// An axis within this many degrees of the viewing ray (either sign) collapses
// into a stub dominated by its arrowhead — noise, not direction — so it is
// culled in 3D before projecting.
const AXIS_VIEW_ALIGNMENT_CULL_DEG = 30;

const COS_AXIS_VIEW_ALIGNMENT_CULL = Math.cos(
  (AXIS_VIEW_ALIGNMENT_CULL_DEG * Math.PI) / 180,
);

// A projected segment shorter than this is a point, with no direction for an
// arrowhead to take.
const MIN_PROJECTED_LEN_PX = 1e-3;

const AXIS_KEYS: readonly GraspAxisKey[] = ['x', 'y', 'z'];

/**
 * Project a resolved pose (robot frame) into the camera image: its origin, and
 * a fixed metric length along each of its display axes — the same remap
 * (`DISPLAY_AXES`) the offset transform rides in, so the arrows point the way
 * the transform's X/Y/Z move the detection. Null when the pose is behind the
 * camera.
 */
export function projectGrasp(
  pose: CartesianPose,
  cameraIntrinsics: CameraIntrinsics,
  cameraPose: CartesianPose,
  axisLengthM: number = DEFAULT_AXIS_LENGTH_M,
): ProjectedGrasp | null {
  const origin = new Vector3(pose.x, pose.y, pose.z);
  const originPx = projectPointToPixel(origin, cameraIntrinsics, cameraPose);

  if (!originPx) return null;

  const q = new Quaternion(pose.i, pose.j, pose.k, pose.w);

  // The angle that foreshortens an axis is the one to this detection's own
  // viewing ray, not the optical axis, so off-centre detections cull correctly.
  const viewDir = origin
    .clone()
    .sub(new Vector3(cameraPose.x, cameraPose.y, cameraPose.z))
    .normalize();

  const axes: ProjectedGrasp['axes'] = [];

  for (const key of AXIS_KEYS) {
    const dir = DISPLAY_AXES[key].clone().applyQuaternion(q);

    if (Math.abs(dir.dot(viewDir)) > COS_AXIS_VIEW_ALIGNMENT_CULL) continue;

    const tipPx = projectPointToPixel(
      origin.clone().addScaledVector(dir, axisLengthM * AXIS_LENGTH_SCALE),
      cameraIntrinsics,
      cameraPose,
    );

    if (!tipPx) continue;

    if (
      Math.hypot(tipPx.x - originPx.x, tipPx.y - originPx.y) <
      MIN_PROJECTED_LEN_PX
    ) {
      continue;
    }

    axes.push({ key, tip: { x: tipPx.x, y: tipPx.y } });
  }

  return { origin: { x: originPx.x, y: originPx.y }, axes };
}
